import { useState, useEffect, useRef } from 'react';
import { useToneAnalysis } from '@/hooks/useToneAnalysis';
import type { ToneScore } from '@/types/coparenting';

interface ToneMeterProps {
  draft: string;
  householdId: string | null;
  onSuggestionApply: (suggestion: string) => void;
}

const BORDER_COLOR: Record<ToneScore, string> = {
  neutral: 'border-green-500 focus:border-green-500',
  tense: 'border-amber-500 focus:border-amber-500',
  accusatory: 'border-red-500 focus:border-red-500',
};

const BANNER_COLOR: Record<ToneScore, string> = {
  neutral: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200',
  tense: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200',
  accusatory: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
};

const TONE_LABEL: Record<ToneScore, string> = {
  neutral: 'Neutral tone',
  tense: 'Tense tone detected',
  accusatory: 'Accusatory tone detected',
};

export function ToneMeter({ draft, householdId, onSuggestionApply }: ToneMeterProps) {
  const { analyzeTone, loading } = useToneAnalysis(householdId);
  const [classification, setClassification] = useState<ToneScore | null>(null);
  const [suggestion, setSuggestion] = useState<string | undefined>(undefined);
  const [reasoning, setReasoning] = useState<string | undefined>(undefined);
  const [dismissed, setDismissed] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!draft.trim() || draft.trim().length < 10) {
      setClassification(null);
      setSuggestion(undefined);
      setReasoning(undefined);
      setDismissed(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const result = await analyzeTone(draft);
      if (result) {
        setClassification(result.classification);
        setSuggestion(result.suggestion);
        setReasoning(result.reasoning);
        setDismissed(false);
      }
    }, 800);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps

  const borderClass = classification ? BORDER_COLOR[classification] : 'border-slate-200 dark:border-slate-700 focus:border-brand';

  return (
    <div>
      {/* Textarea border indicator */}
      <div className={`relative rounded-xl border-2 transition-colors duration-300 ${borderClass}`}>
        {loading && (
          <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-slate-300 dark:bg-slate-600 animate-pulse" />
        )}
      </div>

      {/* Tone banner */}
      {classification && !dismissed && (classification === 'tense' || classification === 'accusatory') && (
        <div className={`mt-2 rounded-xl border p-3 text-sm ${BANNER_COLOR[classification]}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <p className="font-bold">{TONE_LABEL[classification]}</p>
              {reasoning && <p className="mt-1 text-xs opacity-80">{reasoning}</p>}
              {suggestion && (
                <button
                  type="button"
                  onClick={() => {
                    onSuggestionApply(suggestion);
                    setDismissed(true);
                  }}
                  className="mt-2 text-xs font-bold underline underline-offset-2 hover:opacity-70"
                >
                  Use suggested rewrite
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="opacity-60 hover:opacity-100 text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
